import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Calendar, Users, FileText, Send, Palette, TrendingUp, Bell,
  ArrowRight, Clock, CheckSquare, Share2, Headphones, Building2,
  User, Star, Crown, Gift, Activity, Mail, Eye, MousePointerClick
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import Skeleton from '../../components/Skeleton';
import AdminEmptyState from '../../components/admin/AdminEmptyState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [benefitsEnabled, setBenefitsEnabled] = useState(null);
  const [benefitsSaving, setBenefitsSaving] = useState(false);
  const [presence, setPresence] = useState(null);
  const [commsAnalytics, setCommsAnalytics] = useState(null);

  const isSuperAdmin = user?.role === 'super_admin';

  useEffect(() => {
    fetchDashboardData();
    fetchCommsInsights();
    if (isSuperAdmin) fetchBenefitsSetting();
     
  }, [isSuperAdmin]);

  const fetchCommsInsights = async () => {
    try {
      const [presenceRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/presence`, { withCredentials: true }).catch(() => null),
        axios.get(`${API}/admin/communications/analytics`, { withCredentials: true }).catch(() => null),
      ]);
      if (presenceRes?.data) setPresence(presenceRes.data);
      if (analyticsRes?.data) setCommsAnalytics(analyticsRes.data);
    } catch { /* silent */ }
  };

  const fetchBenefitsSetting = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings/member-benefits`, { withCredentials: true });
      setBenefitsEnabled(!!res.data?.enabled);
    } catch { /* silent */ }
  };

  const toggleBenefits = async () => {
    if (benefitsEnabled === null) return;
    setBenefitsSaving(true);
    try {
      const next = !benefitsEnabled;
      await axios.put(`${API}/admin/settings/member-benefits`, { enabled: next }, { withCredentials: true });
      setBenefitsEnabled(next);
      toast.success(next ? 'Member Deals directory enabled' : 'Member Deals directory disabled');
    } catch { toast.error('Failed to update setting'); }
    finally { setBenefitsSaving(false); }
  };

  const fetchDashboardData = async () => {
    try {
      const [statsRes, eventsRes, announcementsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/events?limit=5`, { withCredentials: true }),
        axios.get(`${API}/announcements?active=true`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      setRecentEvents(eventsRes.data);
      setAnnouncements(announcementsRes.data);
    } catch (error) {
      // error handled silently
    }
  };

  const statCards = [
    { label: 'Total Members', value: stats?.total_members || 0, icon: Users, color: 'bg-blue-500', link: '/admin/members' },
    { label: 'Business Members', value: stats?.business_members || 0, icon: Building2, color: 'bg-indigo-500', link: '/admin/members' },
    { label: 'Individual Members', value: stats?.individual_members || 0, icon: User, color: 'bg-cyan-500', link: '/admin/members' },
    { label: 'Pending Activation', value: stats?.pending_activation || 0, icon: Clock, color: 'bg-amber-500', link: '/admin/members' },
    { label: 'Activated Members', value: stats?.activated_members || 0, icon: CheckSquare, color: 'bg-green-500', link: '/admin/members' },
    { label: 'Founding Members', value: stats?.founding_members || 0, icon: Star, color: 'bg-rose-500', link: '/admin/members' },
    { label: 'Upcoming Events', value: stats?.upcoming_events || 0, icon: Calendar, color: 'bg-emerald-500', link: '/admin/events' },
    { label: 'Blog Posts', value: stats?.published_posts || 0, icon: FileText, color: 'bg-teal-500', link: '/admin/blog' },
    { label: 'Community Posts', value: stats?.community_posts || 0, icon: TrendingUp, color: 'bg-purple-500', link: '/admin/members' },
    { label: 'Leadership', value: stats?.total_leadership || 0, icon: Crown, color: 'bg-[#002249]', link: '/admin/leadership' },
  ];

  const pendingItems = [
    { label: 'Blog Submissions', value: stats?.pending_blog_submissions || 0, icon: CheckSquare, link: '/admin/blog-review', color: 'text-amber-600 bg-amber-50 border-amber-200' },
    { label: 'Social Requests', value: stats?.pending_social_requests || 0, icon: Share2, link: '/admin/social-review', color: 'text-pink-600 bg-pink-50 border-pink-200' },
    { label: 'Committee Apps', value: stats?.pending_committee_apps || 0, icon: Users, link: '/admin/committee-apps', color: 'text-purple-600 bg-purple-50 border-purple-200' },
    { label: 'Support Messages', value: stats?.unread_support_messages || 0, icon: Headphones, link: '/admin/support', color: 'text-orange-600 bg-orange-50 border-orange-200' },
    { label: 'Member Requests', value: stats?.pending_member_requests || 0, icon: Send, link: '/admin/marketing-requests', color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { label: 'Marketing Requests', value: stats?.pending_marketing_requests || 0, icon: Palette, link: '/admin/marketing-requests', color: 'text-green-600 bg-green-50 border-green-200' },
  ];

  const totalPending = pendingItems.reduce((sum, item) => sum + item.value, 0);

  return (
    <div className="admin-page" data-testid="admin-dashboard">
      {/* Header */}
      <header className="admin-page-header" data-testid="dashboard-header">
        <div className="admin-page-header__title">
          <p className="admin-eyebrow">Overview</p>
          <h1 className="admin-h1 mt-0.5">
            Welcome back, {user?.name?.split(' ')[0] || 'Admin'}
          </h1>
          <p className="admin-subtitle mt-1.5">Here's what's happening with your chamber today.</p>
        </div>
      </header>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-7">
        {stats === null
          ? Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="admin-stat"
                data-testid={`stat-skeleton-${i}`}
              >
                <Skeleton className="w-9 h-9 rounded-md mb-3" />
                <Skeleton className="h-6 w-1/2 mb-2" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))
          : statCards.map((stat, index) => (
          <Link key={stat.label} to={stat.link}>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="admin-stat"
              data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className={`admin-stat__icon ${stat.color}`}>
                <stat.icon className="w-[17px] h-[17px] text-white" />
              </div>
              <p className="admin-stat__value">{stat.value}</p>
              <p className="admin-stat__label">{stat.label}</p>
            </motion.div>
          </Link>
        ))
        }
      </div>

      {/* Feature Toggles (Super Admin only) */}
      {isSuperAdmin && benefitsEnabled !== null && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-6 bg-white border border-slate-200 rounded-md p-4 flex items-center gap-3"
          data-testid="feature-toggles-card"
        >
          <div className="w-9 h-9 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Gift className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-[#002249]">Member Deals directory</p>
            <p className="text-[12px] text-slate-500 leading-snug">
              Members can offer discounts to each other. Disable to hide the directory platform-wide.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={benefitsEnabled}
            onClick={toggleBenefits}
            disabled={benefitsSaving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors flex-shrink-0 ${benefitsEnabled ? 'bg-emerald-600' : 'bg-slate-300'} ${benefitsSaving ? 'opacity-60' : ''}`}
            data-testid="admin-toggle-member-benefits"
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${benefitsEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </motion.div>
      )}

      {/* Live Activity + Communications Insights */}
      {(presence?.stats || commsAnalytics?.invites) && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6"
          data-testid="live-insights-grid"
        >
          {/* Presence card */}
          {presence?.stats && (
            <Link to="/admin/presence" className="group">
              <div className="u-card bg-white border border-slate-200 rounded-xl p-4 hover:border-[#002249]/30 h-full" data-testid="dashboard-presence-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                      <Activity className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#002249]">Live Activity</p>
                      <p className="text-[11px] text-slate-500">Members active in the last 5 minutes</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#002249] group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-2 bg-emerald-50 rounded text-center" data-testid="db-stat-online">
                    <p className="text-[18px] font-bold text-emerald-700 leading-none">{presence.stats.online_now}</p>
                    <p className="text-[9.5px] font-semibold text-emerald-700/80 uppercase tracking-wide mt-1">Online Now</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-[18px] font-bold text-[#002249] leading-none">{presence.stats.active_today}</p>
                    <p className="text-[9.5px] font-semibold text-slate-600 uppercase tracking-wide mt-1">Today</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-[18px] font-bold text-[#002249] leading-none">{presence.stats.active_7d}</p>
                    <p className="text-[9.5px] font-semibold text-slate-600 uppercase tracking-wide mt-1">7 Days</p>
                  </div>
                  <div className="p-2 bg-slate-50 rounded text-center">
                    <p className="text-[18px] font-bold text-slate-700 leading-none">{presence.stats.total}</p>
                    <p className="text-[9.5px] font-semibold text-slate-600 uppercase tracking-wide mt-1">Total</p>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Communications funnel */}
          {commsAnalytics?.invites && (
            <Link to="/admin/invitations" className="group">
              <div className="u-card bg-white border border-slate-200 rounded-xl p-4 hover:border-[#002249]/30 h-full" data-testid="dashboard-comms-card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-[13px] font-bold text-[#002249]">Invitation Funnel</p>
                      <p className="text-[11px] text-slate-500">Open · Click · Activation rates</p>
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#002249] group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="p-2 bg-amber-50 rounded text-center" data-testid="db-stat-open-rate">
                    <div className="flex items-center justify-center gap-1 text-amber-700">
                      <Eye className="w-3 h-3" />
                      <p className="text-[18px] font-bold leading-none">{commsAnalytics.invites.open_rate}%</p>
                    </div>
                    <p className="text-[9.5px] font-semibold text-amber-700/80 uppercase tracking-wide mt-1">Open</p>
                  </div>
                  <div className="p-2 bg-sky-50 rounded text-center" data-testid="db-stat-click-rate">
                    <div className="flex items-center justify-center gap-1 text-sky-700">
                      <MousePointerClick className="w-3 h-3" />
                      <p className="text-[18px] font-bold leading-none">{commsAnalytics.invites.click_rate}%</p>
                    </div>
                    <p className="text-[9.5px] font-semibold text-sky-700/80 uppercase tracking-wide mt-1">Click</p>
                  </div>
                  <div className="p-2 bg-emerald-50 rounded text-center" data-testid="db-stat-activation-rate">
                    <div className="flex items-center justify-center gap-1 text-emerald-700">
                      <CheckSquare className="w-3 h-3" />
                      <p className="text-[18px] font-bold leading-none">{commsAnalytics.invites.activation_rate}%</p>
                    </div>
                    <p className="text-[9.5px] font-semibold text-emerald-700/80 uppercase tracking-wide mt-1">Activated</p>
                  </div>
                </div>
              </div>
            </Link>
          )}
        </motion.div>
      )}

      {/* Pending Review Section */}
      {totalPending > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
        >
          <h2 className="admin-h2 mb-3 flex items-center">
            <Clock className="w-4 h-4 mr-2 text-amber-500" />
            Needs Your Attention
            <span className="admin-pill admin-pill--amber ml-2">{totalPending}</span>
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {pendingItems.filter(item => item.value > 0).map((item, index) => (
              <Link key={item.label} to={item.link}>
                <div className={`p-3 border rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all ${item.color}`} data-testid={`pending-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="flex items-center justify-between">
                    <item.icon className="w-5 h-5" />
                    <span className="text-2xl font-bold">{item.value}</span>
                  </div>
                  <p className="text-xs font-semibold mt-1">{item.label}</p>
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Recent Events */}
        <div className="lg:col-span-2">
          <div className="admin-card">
            <div className="admin-card__header">
              <h2 className="admin-h2">Upcoming Events</h2>
              <Link to="/admin/events">
                <Button variant="ghost" size="sm" className="text-[#002249] hover:bg-slate-100" data-testid="view-all-events">
                  View All
                  <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recentEvents.length > 0 ? recentEvents.map((event, index) => (
                <div key={event.id} className="px-5 py-4 hover:bg-slate-50/70 transition-colors" data-testid={`recent-event-${index}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3">
                      <div className="w-12 h-12 bg-[#002249] rounded-lg flex flex-col items-center justify-center flex-shrink-0 shadow-[0_2px_6px_rgba(0,34,73,0.18)]">
                        <span className="text-[14px] font-bold text-white leading-none">{event.date?.split('-')[2]}</span>
                        <span className="text-[10px] text-white/75 mt-0.5 uppercase tracking-wide">{new Date(event.date).toLocaleString('en', { month: 'short' })}</span>
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#002249] text-[14px]">{event.title}</h4>
                        <p className="text-[12.5px] text-slate-500 flex items-center mt-1">
                          <Clock className="w-3 h-3 mr-1" />
                          {event.time} • {event.location}
                        </p>
                      </div>
                    </div>
                    {event.is_featured && (
                      <span className="admin-pill admin-pill--accent">
                        Featured
                      </span>
                    )}
                  </div>
                </div>
              )) : (
                <div className="p-6">
                  <AdminEmptyState
                    icon={Calendar}
                    title="No upcoming events"
                    body="Create an event to start filling this list and engaging members."
                    testid="empty-events"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Announcements */}
        <div className="lg:col-span-1 space-y-5">
          <div className="admin-card">
            <div className="admin-card__header">
              <h2 className="admin-h2">Active Announcements</h2>
              <Link to="/admin/announcements">
                <Button variant="ghost" size="sm" className="text-[#002249] hover:bg-slate-100" data-testid="view-all-announcements">
                  Manage
                </Button>
              </Link>
            </div>
            <div className="divide-y divide-slate-100">
              {announcements.length > 0 ? announcements.slice(0, 5).map((announcement, index) => (
                <div key={announcement.id} className="px-5 py-4" data-testid={`announcement-${index}`}>
                  <div className="flex items-start space-x-3">
                    <Bell className="w-4 h-4 text-[#E63946] flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-[#002249] text-[13px]">{announcement.title}</h4>
                      <p className="text-[12px] text-slate-500 mt-1 line-clamp-2 leading-relaxed">{announcement.content}</p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="p-5">
                  <AdminEmptyState
                    icon={Bell}
                    title="No active announcements"
                    body="Pin an announcement to highlight important updates."
                    testid="empty-announcements"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="admin-card">
            <div className="admin-card__header">
              <h2 className="admin-h2">Quick Actions</h2>
            </div>
            <div className="p-4 space-y-2">
              <Link to="/admin/events" className="block">
                <button className="admin-btn admin-btn--secondary w-full justify-start" data-testid="quick-create-event">
                  <Calendar className="w-4 h-4" />
                  Create Event
                </button>
              </Link>
              <Link to="/admin/members" className="block">
                <button className="admin-btn admin-btn--secondary w-full justify-start" data-testid="quick-add-member">
                  <Users className="w-4 h-4" />
                  Add Member
                </button>
              </Link>
              <Link to="/admin/blog" className="block">
                <button className="admin-btn admin-btn--secondary w-full justify-start" data-testid="quick-create-post">
                  <FileText className="w-4 h-4" />
                  Write Blog Post
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
